import React, { useEffect, useState } from 'react';
import { Box, IconButton, Input, Typography } from '@mui/joy';
import { Trash } from '@phosphor-icons/react';
import type { ApiCartLine } from './ApiOrderCart';

export const cartLineUnit = (line: ApiCartLine) =>
  `${line.uom}${line.length_value != null ? ` · ${line.length_value} ${line.length_uom || ''}` : ''}`;

function Quantity({ line, onChange }: { line: ApiCartLine; onChange: (id: string, quantity: number) => void }) {
  const [value, setValue] = useState(String(line.quantity));
  useEffect(() => setValue(String(line.quantity)), [line.quantity]);
  const commit = () => {
    const quantity = Number(value);
    if (Number.isSafeInteger(quantity) && quantity > 0) onChange(line.lineId, quantity);
    else setValue(String(line.quantity));
  };
  return <Input size="sm" type="number" value={value}
    slotProps={{ input: { min: 1, step: 1, 'aria-label': `Quantity for ${line.description}, ${cartLineUnit(line)}` } }}
    onChange={event => setValue(event.target.value)} onBlur={commit}
    onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); commit(); } }}
    endDecorator={<Typography level="body-xs">{line.uom}</Typography>} sx={{ width: 124 }} />;
}

export default function CatalogOrderPanel({ lines, onQuantityChange, onRemove }: {
  lines: ApiCartLine[];
  onQuantityChange: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}) {
  return <Box component="aside" aria-label="Current order" data-sentry-block sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography level="title-lg">Your order</Typography>
      <Typography level="body-sm" color="neutral" role="status">{lines.length} order {lines.length === 1 ? 'line' : 'lines'} · quantities by unit</Typography>
    </Box>
    <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {!lines.length && <Typography level="body-sm" color="neutral" sx={{ p: 2 }}>Items you add appear here. Choose a product to start your order.</Typography>}
      {lines.map(line => <Box key={line.lineId} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level="title-sm" sx={{ overflowWrap: 'anywhere' }}>{line.description}</Typography>
        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{line.item_number} · {cartLineUnit(line)}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Quantity line={line} onChange={onQuantityChange} />
          <IconButton size="sm" variant="plain" color="neutral" aria-label={`Remove ${line.description}, ${cartLineUnit(line)}`} onClick={() => onRemove(line.lineId)}><Trash /></IconButton>
        </Box>
      </Box>)}
    </Box>
    <Typography level="body-xs" color="neutral" sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>Select Done to review current prices and delivery details before submitting.</Typography>
  </Box>;
}
